
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, MessageCircle, Phone, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { ProductData, formatDate } from '@/hooks/useProductDetail';
import { useMessageSeller } from '@/hooks/useMessageSeller';
import UserReviewsDisplay from '@/components/reviews/UserReviewsDisplay';
import ContactOptionsDialog from '@/components/product/ContactOptionsDialog';

interface ProductDetailTabsProps {
  product: ProductData;
  defaultTab?: string;
}

export const ProductDetailTabs: React.FC<ProductDetailTabsProps> = ({ 
  product, 
  defaultTab = "description"
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const handleMessageSeller = useMessageSeller(
    product.id, 
    product.user_id || product.seller?.id
  );

  return (
    <Tabs defaultValue={defaultTab} className="w-full">
      <TabsList className="w-full border-b border-gray-200 pb-0 mb-6">
        <TabsTrigger value="description" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-500">
          Përshkrimi
        </TabsTrigger>
        <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-500">
          Detaje
        </TabsTrigger>
        <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-500">
          Vlerësimet
        </TabsTrigger>
        <TabsTrigger value="location" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-500">
          Vendndodhja
        </TabsTrigger>
        <TabsTrigger value="contact" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-500">
          Kontakto
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="description" className="mt-0">
        <div className="prose max-w-none">
          <p className="text-gray-700 leading-relaxed">
            {product?.description}
          </p>
        </div>
      </TabsContent>
      
      <TabsContent value="details" className="mt-0">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-medium text-gray-900 mb-4">Karakteristikat</h3>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className="text-brand-500 mr-2">•</span>
              <span>Category: {product?.category?.name || 'Not specified'}</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-500 mr-2">•</span>
              <span>Location: {product?.location}</span>
            </li>
            <li className="flex items-start">
              <span className="text-brand-500 mr-2">•</span>
              <span>Posted: {formatDate(product?.created_at || '')}</span>
            </li>
          </ul>
        </div>
      </TabsContent>
      
      <TabsContent value="reviews" className="mt-0">
        <div className="bg-white rounded-lg p-6">
          <h3 className="text-lg font-semibold mb-4">Vlerësimet e Shitësit</h3>
          {product.seller?.id ? (
            <UserReviewsDisplay userId={product.seller.id} />
          ) : product.user_id ? (
            <UserReviewsDisplay userId={product.user_id} />
          ) : (
            <div className="text-center py-4 text-gray-500">
              Nuk mund të shfaqen vlerësimet për këtë përdorues.
            </div>
          )}
        </div>
      </TabsContent>
      
      <TabsContent value="location" className="mt-0">
        <div className="rounded-lg overflow-hidden aspect-video bg-gray-100 flex items-center justify-center">
          <div className="text-center p-6">
            <MapPin size={32} className="mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600">Harta do të shfaqet këtu</p>
            <p className="text-sm text-gray-500">{product?.location}, Shqipëri</p>
          </div>
        </div>
      </TabsContent>
      
      <TabsContent value="contact" className="mt-0">
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="font-medium text-gray-900 mb-4">Kontakto shitësin</h3>
          
          <div className="space-y-4">
            <p>Zgjidhni një nga opsionet e kontaktit për të komunikuar me shitësin.</p>
            
            <div className="flex gap-4 flex-col sm:flex-row">
              {isAuthenticated && (
                <Button 
                  className="flex-1 bg-brand-500 hover:bg-brand-600"
                  onClick={handleMessageSeller}
                >
                  <MessageCircle size={18} className="mr-2" />
                  Dërgo Mesazh
                </Button>
              )}
              
              <ContactOptionsDialog
                phone={product.phone}
                phoneCountryCode={product.phone_country_code}
                whatsappEnabled={product.whatsapp_enabled}
                viberEnabled={product.viber_enabled}
              >
                <Button variant="outline" className="flex-1">
                  <Phone size={18} className="mr-2" />
                  Opsionet e Kontaktit
                </Button>
              </ContactOptionsDialog>
            </div>

            {!isAuthenticated && (
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  Për të dërguar mesazhe në platformë, ju duhet të{' '}
                  <Button variant="link" onClick={() => navigate('/auth')} className="p-0 h-auto text-blue-700 underline">
                    identifikoheni
                  </Button>
                </p>
              </div>
            )}
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
};
